import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/SEO/MetaTags';
import { pageConfigs, generateBlogPostSchema } from '../../utils/seo';

const AIScribesHealthcare: React.FC = () => {
  // Generate structured data for the blog post
  const blogSchema = generateBlogPostSchema({
    title: "The Rise of AI Scribes in Healthcare: Why Notation Is Poised to Lead Physical Therapy Into the Future",
    description: "Discover how Notation by Fownd goes beyond generic AI scribes, offering PT-specific intelligence for clinical documentation, workflow support, and compliance.",
    datePublished: "2025-07-12",
    dateModified: "2025-07-12",
    author: "Rohan Sai Nalla",
    url: "/blogs/ai-scribes-healthcare-notation-physical-therapy-future",
    image: "/blog/default.jpg"
  });

  return (
    <>
      <MetaTags
        title={pageConfigs.blogAIScribesHealthcare.title}
        description={pageConfigs.blogAIScribesHealthcare.description}
        keywords={pageConfigs.blogAIScribesHealthcare.keywords}
        canonical={pageConfigs.blogAIScribesHealthcare.canonical}
        ogImage="/blog/default.jpg"
        ogType="article"
        schemaData={blogSchema}
        additionalMeta={[
          { property: "article:author", content: "Rohan Sai Nalla" },
          { property: "article:published_time", content: "2025-07-12T00:00:00Z" },
          { property: "article:modified_time", content: "2025-07-12T00:00:00Z" },
          { property: "article:section", content: "Healthcare Technology" },
          { property: "article:tag", content: "AI Scribes" },
          { property: "article:tag", content: "Physical Therapy Documentation" },
          { property: "article:tag", content: "Healthcare AI" },
          { property: "article:tag", content: "Clinical Documentation" },
          { property: "article:tag", content: "Notation" },
          { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" }
        ]}
      />
      
      <div className="bg-white">
        {/* Hero section with title */}
        <div className="bg-darkGreen text-white pt-36 pb-8 mb-16 relative overflow-hidden">
          <div className="relative z-10">
            <div className="max-w-3xl mx-auto text-center px-4">
              <h4 className="text-emerald-400 font-medium mb-2">Blog Post</h4>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4 font-productSans">
                The Rise of AI Scribes in Healthcare: Why Notation Is Poised to Lead Physical Therapy Into the Future
              </h1>
              <p className="text-emerald-100 text-lg">
                Purpose-built for physical therapy, Notation functions like your personal PT-GPT: a domain-specialized, multimodal AI assistant
              </p>
              <p className="text-emerald-200 text-sm mt-4">
                By Rohan Sai Nalla • July 12, 2025
              </p>
            </div>
          </div>
        </div>

        {/* Blog content */}
        <div className="max-w-3xl mx-auto px-4 pb-16">
          {/* Back button at top */}
          <div className="mb-8">
            <Link 
              to="/blogs" 
              className="inline-flex items-center text-emerald-600 hover:text-emerald-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blogs
            </Link>
          </div>

          {/* Article content */}
          <article className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-6">
              In the rapidly evolving world of healthcare, AI scribes are no longer an experiment—they're a proven necessity. But Notation by Fownd aims to be much more than just a medical scribe. Purpose-built for physical therapy, it functions like your personal PT-GPT: a domain-specialized, multimodal AI assistant that understands the unique complexity of PT documentation, clinical reasoning, and payer requirements.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Market Context — AI Scribes Are Just the Beginning</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              The healthcare industry is witnessing explosive growth in the use of AI-powered clinical assistants. Major health systems like Kaiser Permanente have rolled out AI scribes to over 3,400 doctors in just 10 weeks, citing time savings of over an hour per day per clinician. Satisfaction scores have topped 80% among both patients and providers.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              But for physical therapists, this is just the starting line.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Generic AI scribes weren't built for the rhythm of rehab care. They often overlook the nuances of physical therapy assessments, interventions, and outcome measures. That's why Notation is built differently: it's not just a scribe—it's PT-GPT.
            </p>

            <div className="bg-emerald-50 rounded-lg p-6 mb-8 border-l-4 border-emerald-600">
              <p className="text-gray-700 leading-relaxed mb-4">
                Notation brings domain-specific intelligence to PT documentation. From muscle testing to SOAP structuring to ICD/CPT mapping, it's trained to speak the language of physical therapy, not just medicine. Where traditional scribes passively transcribe, Notation proactively supports every phase of the clinical workflow—while remaining editable, explainable, and fully therapist-controlled.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What Makes Notation Different</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Notation isn't a ChatGPT wrapper. It runs on a self-hosted, privacy-forward stack utilizing the latest foundational large language models (LLMs), fine-tuned specifically for PT workflows. Built to accept voice (and soon, video) inputs, it offers real-time feedback during evaluations while generating clean, accurate, compliant documentation.
            </p>

            <p className="text-gray-700 leading-relaxed mb-4">
              Here's how:
            </p>

            <div className="bg-blue-50 rounded-lg p-6 mb-8">
              <ul className="space-y-4 text-gray-700">
                <li>
                  <strong className="text-blue-800">Smart Cue Cards:</strong> As PTs move through their evaluations, cue cards light up in Notation, guiding them to hit payer-required elements without missing a beat.
                </li>
                <li>
                  <strong className="text-blue-800">Voice-Driven Objective Input:</strong> PTs can dictate goniometric and strength values, instantly populating structured tables. No toggling between tabs, no lost measurements.
                </li>
                <li>
                  <strong className="text-blue-800">Real-Time Summaries:</strong> Notation updates SOAP summaries as the session unfolds, and adapts instantly when clinicians make edits.
                </li>
                <li>
                  <strong className="text-blue-800">Built-in Assessment and Plan Generator:</strong> PTs can edit or override AI-generated assessments and treatment plans, all structured for payer compliance.
                </li>
                <li>
                  <strong className="text-blue-800">HEP Export:</strong> Plans are outputted in clear formats, ready to be printed or handed to the patient.
                </li>
                <li>
                  <strong className="text-blue-800">CPT & ICD Coding Built-In:</strong> No more lookups. Notation suggests compliant codes, calculates units, and flags potential modifier issues.
                </li>
                <li>
                  <strong className="text-blue-800">Custom EMR Plugins:</strong> With a single click, clinicians can push notes into their EMR. Our browser plugin architecture is built for adaptability, whether a clinic uses Jane, WebPT, Cliniko, or something homegrown.
                </li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Trust, Security & Compliance</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Notation is built from the ground up on SOC 2 and HIPAA-compliant infrastructure. We are working with Scrut to formalize certifications. And unlike many general-purpose AI scribes:
            </p>

            <div className="bg-green-50 rounded-lg p-6 mb-8">
              <ul className="space-y-3 text-gray-700">
                <li>No PII or PHI required to use</li>
                <li>No audio/video storage unless explicitly needed by the clinic</li>
                <li>All data is encrypted in transit and at rest</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Designed to Support, Not Replace</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Notation doesn't just reduce documentation time—it elevates the clinical experience. The technology and user experience are bar none, offering a streamlined, intuitive interface that adapts to a PT's individual style. For younger clinicians, Notation acts as a vital skill enhancement tool, guiding them through evaluations with embedded best practices and structured templates. For seasoned therapists, it serves as a reference aid and performance enhancer, helping them run faster, more efficient sessions without compromising quality.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              This kind of design intelligence means more than just time saved—it translates to higher throughput, improved clinic revenue, and better patient satisfaction.
            </p>

            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg p-6 border border-emerald-100 mb-8">
              <p className="text-gray-700 leading-relaxed mb-4 font-semibold">
                Fownd's philosophy is simple: PTs should stay in control. Notation is not here to automate away clinical reasoning. It's built to support therapists by:
              </p>
              <ul className="space-y-2 text-gray-700 ml-4">
                <li>Making the documentation burden lighter</li>
                <li>Providing insurance-ready summaries</li>
                <li>Giving clinicians the ability to override and customize every word</li>
              </ul>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Risks of Getting It Wrong</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              Many AI scribes claim to be ready for rehab. Few deliver:
            </p>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
              <ul className="space-y-3 text-gray-700">
                <li><strong>Hallucinations in SOAP notes</strong> can lead to poor compliance</li>
                <li><strong>Clunky integrations</strong> frustrate clinic staff</li>
                <li><strong>Medical jargon and accents</strong> trip up general LLMs</li>
              </ul>
            </div>

            <p className="text-gray-700 leading-relaxed mb-8">
              Notation was trained on real PT evaluations and continues to improve with clinician feedback. It supports accents, noisy gyms, and real-life workflows. And we're just getting started: upcoming computer vision support will bring movement analysis and remote patient monitoring to life.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">The Future Is Hands-On + Hands-Free</h2>

            <p className="text-gray-700 leading-relaxed mb-6">
              AI in healthcare isn't about removing clinicians. It's about freeing them to do their best work. Notation keeps the PT front and center, while helping them:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Document faster</h3>
                <p className="text-green-700">Real-time documentation during sessions, not after.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Bill more accurately</h3>
                <p className="text-green-700">Built-in CPT & ICD coding with compliance checks.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Stay compliant</h3>
                <p className="text-green-700">HIPAA-compliant infrastructure with SOC 2 certification in progress.</p>
              </div>
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-800 mb-3">Focus on patients</h3>
                <p className="text-green-700">Spend more time with patients, less time on documentation.</p>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-8">
              If you're a physical therapist or clinic owner ready to see what AI can do for your workflow—without compromising your quality of care—Notation is ready.
            </p>
          </article>

          {/* Try Notation CTA Banner */}
          <div className="mt-16 p-8 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-100">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Experience AI Documentation Built for PTs?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                👉 Try Notation for your next evaluation — no strings attached.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://notation.fownd.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors inline-flex items-center justify-center"
                >
                  Try Notation
                </a>
                <Link
                  to="/demo"
                  className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold border-2 border-emerald-600 hover:bg-emerald-50 transition-colors inline-flex items-center justify-center"
                >
                  Watch Demo
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIScribesHealthcare;
